import { useSeoMeta, useHead } from '@unhead/react';
import { Package, ShoppingCart, Check, Box, Shield, Tag, Wrench, Pen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import packingBg from '../../images/VIP House Movers Team Packing.jpg';
import packingImg1 from '../../images/VIP House Movers Removal Boxes.jpg';
import boxesImg from '../../images/VIP House Movers Removal Boxes.jpg';
import wrappingImg from '../../images/VIP House Movers Removal Man Wrapping.jpg';
import sofaWrapImg from '../../images/VIP House Movers Wrapping Sofas.jpg';
import teamCarryImg from '../../images/VIP House Movers Team Carrying Items.jpg';
import wardrobeImg from '../../images/VIP House Movers Team Wrapping Wardrobe.jpg';
import vanImg from '../../images/VIP House Movers Removal Van.jpg';

type Product = {
  name: string;
  description: string;
  icon: typeof Box;
  sizes?: string[];
};

type Bundle = {
  name: string;
  description: string;
  items: string[];
  popular?: boolean;
};

const categories: { title: string; products: Product[] }[] = [
  {
    title: 'Boxes & Storage',
    products: [
      { name: 'Small Moving Box', description: 'Ideal for heavy items like books, tools, and kitchenware.', icon: Box, sizes: ['Small (0.08m³)'] },
      { name: 'Medium Moving Box', description: 'Perfect for toys, lamps, linens, and general household items.', icon: Box, sizes: ['Medium (0.14m³)'] },
      { name: 'Large Moving Box', description: 'Great for lightweight bulky items like duvets, pillows, and clothes.', icon: Box, sizes: ['Large (0.21m³)'] },
      { name: 'Book Box', description: 'Small reinforced box designed to hold heavy books without tearing.', icon: Box, sizes: ['Book Box (0.06m³)'] },
      { name: 'Wardrobe Box', description: 'Includes a hanging rail so clothes can travel on hangers. Keeps garments wrinkle-free.', icon: Box, sizes: ['Wardrobe Box'] },
      { name: 'TV Box', description: 'Adjustable box designed specifically for flat-screen televisions. Extra protective foam included.', icon: Box, sizes: ['32"-55"', '55"+'] },
      { name: 'Mirror & Picture Box', description: 'Reinforced box for framed artwork, mirrors, and photographs.', icon: Box, sizes: ['Small', 'Medium', 'Large'] },
      { name: 'Box Dividers (Cell Kits)', description: 'Separate glasses, bottles, and mugs inside boxes to prevent breakage.', icon: Box, sizes: ['6-cell', '12-cell'] },
    ],
  },
  {
    title: 'Protective Wrapping',
    products: [
      { name: 'Bubble Wrap', description: 'Protects fragile items like glassware, ceramics, and electronics during transit.', icon: Shield, sizes: ['Small Roll (10m)', 'Large Roll (50m)'] },
      { name: 'Packing Paper', description: 'Wraps dishes, ornaments, and delicate items without leaving ink marks.', icon: Shield, sizes: ['5kg Pack', '10kg Pack'] },
      { name: 'Foam Sheets', description: 'Extra cushioning for plates, mirrors, and picture frames.', icon: Shield, sizes: ['Pack of 25', 'Pack of 50'] },
      { name: 'Stretch Wrap (Shrink Wrap)', description: 'Secures furniture drawers, protects upholstery, and bundles loose items together.', icon: Shield, sizes: ['Roll (300m)'] },
      { name: 'Void Fill', description: 'Packing peanuts, air pillows, and crumpled paper to prevent items from shifting inside boxes.', icon: Shield, sizes: ['Small Bag', 'Large Bag'] },
    ],
  },
  {
    title: 'Tape & Tools',
    products: [
      { name: 'Packing Tape', description: 'Heavy-duty tape for securing boxes. Strong adhesion, clear or brown.', icon: Wrench, sizes: ['Roll (50m)', 'Roll (100m)', 'Case of 36'] },
      { name: 'Tape Dispenser', description: 'Makes packing faster and easier with smooth cutting action.', icon: Wrench, sizes: ['Handheld', 'Bench-top'] },
      { name: 'Permanent Markers', description: 'Write contents, room names, and handling instructions on boxes.', icon: Pen, sizes: ['Single', 'Pack of 4', 'Pack of 10'] },
    ],
  },
  {
    title: 'Furniture Protection',
    products: [
      { name: 'Furniture Moving Blankets', description: 'Prevent scratches, scuffs, and dents on furniture and appliances during transit.', icon: Shield, sizes: ['Single', 'Pack of 4', 'Pack of 10'] },
      { name: 'Mattress Bags', description: 'Keep mattresses and mattress protectors clean and dry during transport.', icon: Shield, sizes: ['Single', 'Single Zip', 'King Zip'] },
      { name: 'Sofa & Chair Covers', description: 'Protect upholstered furniture from dirt, dust, and moisture.', icon: Shield, sizes: ['Small', 'Large', 'Extra Large'] },
      { name: 'Corner Protectors', description: 'Safeguard furniture edges and framed items from impact damage.', icon: Shield, sizes: ['Pack of 8', 'Pack of 20'] },
      { name: 'Dust Sheets / Protective Covers', description: 'Protect floors and furniture while moving. Reusable and washable.', icon: Shield, sizes: ['Single', 'Pack of 5', 'Pack of 10'] },
    ],
  },
  {
    title: 'Labelling & Organisation',
    products: [
      { name: 'Labels / Coloured Stickers', description: 'Identify box contents and destination rooms with colour-coded labels.', icon: Tag, sizes: ['Pack of 50', 'Pack of 100', 'Assorted Colours'] },
      { name: 'Zip-Lock Bags', description: 'Store screws, bolts, remote controls, and small hardware. Keep parts organised.', icon: Tag, sizes: ['Mixed Pack (A5–A2)'] },
      { name: 'Cable Ties / Velcro Straps', description: 'Organise electrical cables from TVs, computers, and appliances. Colour-coded options.', icon: Tag, sizes: ['Pack of 20', 'Pack of 50', 'Pack of 100'] },
    ],
  },
  {
    title: 'Moving Equipment',
    products: [
      { name: 'Moving Dolly / Hand Truck', description: 'Makes transporting heavy boxes and appliances easier. 150kg capacity.', icon: Package, sizes: ['Standard (150kg)', 'Heavy Duty (300kg)'] },
      { name: 'Ratchet Straps / Tie-Down Straps', description: 'Secure furniture and boxes inside the moving vehicle to prevent shifting.', icon: Package, sizes: ['2.5m Pair', '5m Pair'] },
      { name: 'Gloves', description: 'Improve grip and protect hands while lifting heavy furniture and boxes.', icon: Package, sizes: ['Small', 'Medium', 'Large', 'XL'] },
    ],
  },
];

const bundles: Bundle[] = [
  {
    name: 'Starter Pack',
    description: 'Ideal for small flats and studio moves.',
    items: ['5 Small Boxes', '5 Medium Boxes', '1 Roll Packing Tape', '1 Roll Bubble Wrap (10m)', '1 Permanent Marker', 'Pack of 50 Labels'],
  },
  {
    name: 'Home Mover Pack',
    description: 'Perfect for 2–3 bedroom house moves.',
    popular: true,
    items: ['10 Small Boxes', '10 Medium Boxes', '5 Large Boxes', '2 Wardrobe Boxes', '2 Rolls Packing Tape', '1 Tape Dispenser', '1 Roll Bubble Wrap (50m)', '1 Pack Packing Paper (10kg)', '1 Pack Foam Sheets (25)', '2 Permanent Markers', '1 Pack Labels (100)', '4 Furniture Blankets'],
  },
  {
    name: 'Family Move Pack',
    description: 'Designed for larger family homes with fragile items.',
    items: ['15 Small Boxes', '15 Medium Boxes', '10 Large Boxes', '5 Book Boxes', '3 Wardrobe Boxes', '1 TV Box', '2 Mirror & Picture Boxes', '4 Rolls Packing Tape', '2 Tape Dispensers', '2 Rolls Bubble Wrap (50m)', '2 Packs Packing Paper (10kg)', '2 Packs Foam Sheets (50)', '1 Roll Stretch Wrap', '1 Bag Void Fill', '4 Permanent Markers', '2 Packs Labels (100)', '8 Furniture Blankets', '2 Mattress Bags', 'Pair of Gloves'],
  },
  {
    name: 'Essentials Bundle',
    description: 'All the protective materials you need in one go.',
    items: ['1 Roll Bubble Wrap (50m)', '1 Pack Packing Paper (10kg)', '1 Pack Foam Sheets (25)', '1 Roll Stretch Wrap', '1 Bag Void Fill', '1 Roll Packing Tape', '1 Tape Dispenser', '1 Permanent Marker'],
  },
  {
    name: 'Furniture Protection Pack',
    description: 'Keep your furniture and appliances in pristine condition.',
    items: ['6 Furniture Blankets', '2 Mattress Bags', '2 Sofa Covers', '1 Pack Corner Protectors (20)', '1 Roll Stretch Wrap', '2 Pairs of Gloves'],
  },
];

export default function PackingSupplies() {
  useSeoMeta({
    title: 'Packing Supplies & Moving Boxes | Buy Moving Materials | VIP House Movers',
    description: 'Buy high-quality packing supplies online including cardboard boxes, bubble wrap, packing tape, furniture blankets and moving bundles. Free delivery on orders over £50. VIP House Movers.',
    ogTitle: 'Packing Supplies & Moving Boxes | VIP House Movers',
    ogDescription: 'Shop professional packing supplies from VIP House Movers. Boxes, tape, bubble wrap, and moving bundles available. Order online today.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/packing-supplies',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Packing Supplies | VIP House Movers',
    twitterDescription: 'Shop professional moving boxes, tape, bubble wrap and more from VIP House Movers.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/packing-supplies');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'packing supplies, moving boxes, bubble wrap, packing tape, furniture blankets, moving supplies, cardboard boxes, wardrobe boxes, packing paper, stretch wrap, moving bundles, VIP House Movers',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Packing Supplies',
              item: 'https://www.viphousemovers.com/packing-supplies',
            },
          ],
        }),
      },
    ],
  });

  return (
    <SiteShell activePath="/packing-supplies" fixedNav>
      <PageHero
        eyebrow="VIP House Movers Supplies"
        title="Professional packing supplies"
        description="Get everything you need for a smooth move — from boxes and tape to full protection kits. Order online for delivery or click & collect."
        align="center"
        primaryLabel="View Our Bundles"
        primaryTo="#bundles"
        secondaryLabel="Contact Us"
        secondaryTo="/contact"
        backgroundImage={packingBg}
      />

      {/* Trust Banner */}
      <div className="bg-white border-b border-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { label: 'Fast Delivery', desc: '1–3 working days' },
              { label: 'Free Delivery', desc: 'Orders over £50' },
              { label: 'Quality Guaranteed', desc: 'Premium moving grade' },
              { label: 'Click & Collect', desc: 'Free, no wait time' },
            ].map((item) => (
              <div key={item.label} className="rounded-xl bg-gray-50 p-4">
                <p className="font-bold text-gray-900">{item.label}</p>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Categories & Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900">Browse our supplies</h2>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Every product is hand-selected for quality, durability, and value. Whether you are moving one room or a whole house, we have the right materials.
          </p>
        </div>

        <div className="space-y-20">
          {categories.map((category, catIdx) => (
            <section key={category.title}>
              {catIdx === 0 && (
                <div className="relative overflow-hidden rounded-2xl mb-8 h-40 md:h-48">
                  <img src={boxesImg} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
                  <h3 className="absolute bottom-6 left-6 text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                    <span className="w-1 h-8 bg-[#D4AF37] rounded-full inline-block" />
                    {category.title}
                  </h3>
                </div>
              )}
              {catIdx === 1 && (
                <div className="relative overflow-hidden rounded-2xl mb-8 h-40 md:h-48">
                  <img src={wrappingImg} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
                  <h3 className="absolute bottom-6 left-6 text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                    <span className="w-1 h-8 bg-[#D4AF37] rounded-full inline-block" />
                    {category.title}
                  </h3>
                </div>
              )}
              {catIdx === 2 && (
                <div className="relative overflow-hidden rounded-2xl mb-8 h-40 md:h-48">
                  <img src={teamCarryImg} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
                  <h3 className="absolute bottom-6 left-6 text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                    <span className="w-1 h-8 bg-[#D4AF37] rounded-full inline-block" />
                    {category.title}
                  </h3>
                </div>
              )}
              {catIdx === 3 && (
                <div className="relative overflow-hidden rounded-2xl mb-8 h-40 md:h-48">
                  <img src={sofaWrapImg} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
                  <h3 className="absolute bottom-6 left-6 text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                    <span className="w-1 h-8 bg-[#D4AF37] rounded-full inline-block" />
                    {category.title}
                  </h3>
                </div>
              )}
              {catIdx === 4 && (
                <div className="relative overflow-hidden rounded-2xl mb-8 h-40 md:h-48">
                  <img src={wardrobeImg} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
                  <h3 className="absolute bottom-6 left-6 text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                    <span className="w-1 h-8 bg-[#D4AF37] rounded-full inline-block" />
                    {category.title}
                  </h3>
                </div>
              )}
              {catIdx === 5 && (
                <div className="relative overflow-hidden rounded-2xl mb-8 h-40 md:h-48">
                  <img src={vanImg} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
                  <h3 className="absolute bottom-6 left-6 text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                    <span className="w-1 h-8 bg-[#D4AF37] rounded-full inline-block" />
                    {category.title}
                  </h3>
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {category.products.map((product) => {
                  const Icon = product.icon;
                  return (
                    <div key={product.name} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-[#D4AF37]/30 transition-all duration-200 p-6">
                      <div className="w-12 h-12 bg-[#E7F2F2] rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-[#0F4A4E]" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{product.name}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{product.description}</p>
                      {product.sizes && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {product.sizes.map((size) => (
                            <span key={size} className="text-xs bg-gray-50 border border-gray-200 rounded-full px-3 py-1 text-gray-600">
                              {size}
                            </span>
                          ))}
                        </div>
                      )}
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F4A4E] hover:text-[#0C3D41] transition-colors"
                      >
                        Enquire about this item <ShoppingCart className="w-4 h-4" />
                      </Link>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* Bundles Section */}
      <section id="bundles" className="bg-gray-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">Save time & money</p>
            <h2 className="mt-4 text-4xl font-bold text-white">Curated moving bundles</h2>
            <p className="mt-4 text-xl text-gray-400 max-w-3xl mx-auto">
              Hand-picked bundles with everything you need for your move — at a better price than buying individually.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {bundles.map((bundle) => (
              <div
                key={bundle.name}
                className={`bg-white rounded-2xl shadow-sm p-8 relative ${bundle.popular ? 'ring-2 ring-[#D4AF37] transform md:-translate-y-2' : 'border border-gray-100'}`}
              >
                {bundle.popular && (
                  <div className="absolute top-0 right-0 bg-[#D4AF37] text-gray-900 font-bold px-3 py-1 text-sm rounded-bl-lg rounded-tr-xl">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{bundle.name}</h3>
                <p className="text-gray-500 mb-6">{bundle.description}</p>
                <ul className="space-y-3 mb-8">
                  {bundle.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-gray-700">
                      <Check className={`w-5 h-5 flex-shrink-0 ${bundle.popular ? 'text-[#D4AF37]' : 'text-green-500'}`} />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center w-full py-3 rounded-lg font-bold transition-all ${
                    bundle.popular
                      ? 'bg-[#D4AF37] text-gray-900 hover:bg-yellow-500'
                      : 'border-2 border-[#D4AF37] text-gray-900 hover:bg-[#D4AF37]'
                  }`}
                >
                  Enquire About This Bundle
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-400">
              Need a custom bundle? <Link to="/contact" className="text-[#D4AF37] hover:text-yellow-500 font-semibold">Contact our team</Link> and we will put together a bespoke pack for your move.
            </p>
          </div>
        </div>
      </section>

      {/* Delivery & Info Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Free delivery', desc: 'On all orders over £50. Standard delivery 1–3 working days.', icon: Package },
              { title: 'Click & collect', desc: 'Order online and pick up from our Wolverhampton depot — free and fast.', icon: Package },
              { title: 'Bulk discounts', desc: 'Planning a large move? Ask about our trade and bulk pricing options.', icon: Package },
              { title: 'Eco-friendly', desc: 'Our boxes are made from recycled materials and are fully recyclable.', icon: Shield },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="text-center p-6 rounded-2xl bg-gray-50 border border-gray-100">
                  <div className="w-14 h-14 bg-[#E7F2F2] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-[#0F4A4E]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 text-white py-20 border-t-8 border-[#D4AF37]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 tracking-tight">Need help choosing?</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto font-light">
            Not sure what you need? Our team can recommend the right boxes, protective materials, and quantities for your move.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-gray-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg shadow-[#D4AF37]/20"
            >
              Speak to Our Team
            </Link>
            <a
              href="tel:01217527107"
              className="inline-flex items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/5 px-8 py-4 text-lg font-bold text-white transition-all duration-300 hover:border-[#B7D1D2] hover:text-[#9ED0D3] backdrop-blur-sm"
            >
              0121 752 7107
            </a>
          </div>
        </div>
      </section>

      {/* Product image at bottom */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <img
          src={packingImg1}
          alt="VIP House Movers professional packing boxes and materials for a house move"
          width={1200}
          height={400}
          loading="lazy"
          decoding="async"
          className="w-full h-48 md:h-64 object-cover rounded-2xl shadow-sm"
        />
      </div>
    </SiteShell>
  );
}
